import {
  dedupeIntegration,
  extraErrorDataIntegration,
  init,
  replayIntegration,
} from '@sentry/react';
import type { Event } from '@sentry/types';

import sentryBrowserFilterOptions from '@rover/utilities/sentryBrowserFilterOptions';

// Don't destructure because webpack.EnvironmentPlugin can't replace destructured vars
const SENTRY_DSN = process.env.SENTRY_DSN; // eslint-disable-line prefer-destructuring
const ROVER_ENVIRONMENT = process.env.ROVER_ENVIRONMENT || 'development';
const ENVIRONMENT = `${ROVER_ENVIRONMENT}-client`;

if (!SENTRY_DSN || !SENTRY_DSN.length) {
  console.error('SENTRY_DSN environment variable was not found'); // eslint-disable-line no-console
}

const FILTERED_ERROR_SAMPLE_RATE = 0.01;

const FILTERED_ERROR_REGEX =
  /Hydration failed|Hydration Error|Text content does not match|Expected server HTML to contain|There was an error while hydrating|server rendered.*didn't match.*client|hydration.*mismatch|the server rendered HTML didn't match|SSR-ed Client Component|server\/client branch|browser extension.*messes.*HTML|react\.dev\/link\/hydration-mismatch|minified react error #(418|423|425)|error-decoder\.html\?invariant=(418|423|425)|message has reached max retries|queuemaxretrieserror|refiner\.io.*Cannot read propert(y|ies).*postMessage|Cannot read propert(y|ies).*postMessage.*refiner\.io|contentWindow.*postMessage.*refiner|InvalidAccessError.*object does not support.*operation/i;

// iOS Safari's microdata extractor (autofill / Reader Mode) throws
// `TypeError: Attempted to assign to readonly property` on Cat in a Flat
// sitter profiles. The throw is internal to Safari and has no user impact;
// the stack frame name is unique so we can drop without collateral. See SSR-T31:
// rovercom.sentry.io/issues/7399945762
const DROPPED_ERROR_REGEX = /extractFilteredSchemaValuesFromMicroData/i;

function isFilteredErrorEvent(event: Event): boolean {
  return FILTERED_ERROR_REGEX.test(JSON.stringify(event));
}

function isDroppedErrorEvent(event: Event): boolean {
  return DROPPED_ERROR_REGEX.test(JSON.stringify(event));
}

init({
  dsn: SENTRY_DSN,
  environment: ENVIRONMENT,
  release: process.env.RELEASE_ID || 'unknown',
  integrations: [dedupeIntegration(), extraErrorDataIntegration(), replayIntegration()],
  beforeSend(event) {
    if (isDroppedErrorEvent(event)) {
      return null;
    }

    // Check if this event contains a filtered error (hydration, queue retries, etc.)
    const hasFilteredError = isFilteredErrorEvent(event);

    if (hasFilteredError) {
      // Only report 1% of filtered errors (1 out of 100)
      if (Math.random() > FILTERED_ERROR_SAMPLE_RATE) {
        return null;
      }

      return {
        ...event,
        tags: { ...event.tags, filtered_error_sampled: true },
      };
    }

    return event; // Send all other errors normally
  },
  initialScope: {
    tags: {
      node_env: process.env.NODE_ENV,
    },
  },
  replaysSessionSampleRate: 0.001,
  replaysOnErrorSampleRate: 0.001,
  ...sentryBrowserFilterOptions,
});
